<template>
    <div>
        <fetch-loader v-if="pending || error" :pending="pending" :error="error" @retry="refresh" height="600"/>
        <template v-else>
            <client-only>
                <template v-if="pendingInvitations.length || invitedCodeJobs.length || pendingRequests.length">
                    <template v-if="pendingInvitations.length || invitedCodeJobs.length">
                        <p class="subheading">{{ $t('label.invitations') }}</p>
                        <expansion-panels open>
                            <expansion-panel v-for="invitation in pendingInvitations" :key="`invitation-${invitation.id}`" :title="invitation.job.title" :subtitle="invitation.job.club?.display_name ?? null">

                                <position-operations-content :position="invitation.job" :group="props.group">
                                    <template v-if="invitation.description" #before_description>
                                        <p class="subheading">{{ $t('label.invitation') }}</p>
                                        <p v-html="invitation.description" class="whitespace-pre-wrap"/>
                                    </template>
                                </position-operations-content>

                                <wrapper>
                                    <form-btn @success="handleDeclinedInvitation(invitation)"
                                              :action="$api('jobs.invitations.decline', invitation.job.id)"
                                              :data="{user_job_id: invitation.id, token: props.token}"
											  :confirm="{title: $t('confirm.job_invitation.decline.title'), message: $t('confirm.job_invitation.decline.message', {job: invitation.job.title}), type: 'warning', inputs: [{type: 'text', name: 'message', placeholder: $t('confirm.job_invitation.decline.reason')}]}"
                                              method="PUT"
                                              color="warning">
                                        <icon :icon="icons.decline"/>
                                        <span>{{ $t('action.decline') }}</span>
                                    </form-btn>

									<btn @click="openAcceptModal(invitation.job, invitation)" color="success">
										<icon :icon="icons.accept"/>
										<span>{{ $t('action.accept_invitation') }}</span>
									</btn>
                                </wrapper>
                            </expansion-panel>
                            <expansion-panel v-for="invitedJob in invitedCodeJobs" :key="`job-${invitedJob.id}`" :title="invitedJob.title" :subtitle="invitedJob.club?.display_name ?? null">
                                <position-operations-content :position="invitedJob" :group="props.group"/>

                                <wrapper>
                                    <form-btn @success="handleDeclinedJob(invitedJob)"
                                              :action="$api('jobs.invitations.decline', invitedJob.id)"
                                              :data="{code: props.code}"
                                              method="PUT"
                                              color="warning">
                                        <icon :icon="icons.decline"/>
                                        <span>{{ $t('action.decline') }}</span>
                                    </form-btn>
                                    <btn @click="openAcceptModal(invitedJob)" color="success">
                                        <icon :icon="icons.accept"/>
                                        <span>{{ $t('action.accept_invitation') }}</span>
                                    </btn>
                                </wrapper>
                            </expansion-panel>
                        </expansion-panels>
                    </template>
                    <template v-if="pendingRequests.length">
                        <p class="subheading">{{ $t('label.requests') }}</p>
                        <expansion-panels open>
                            <expansion-panel v-for="request in pendingRequests" :key="`request-${request.id}`" :title="request.job.title" :subtitle="request.job.club?.display_name ?? null">
                                <position-operations-content :position="request.job" :group="props.group">
                                    <template v-if="request.description" #before_description>
                                        <p class="subheading">{{ $t('label.request') }}</p>
                                        <p v-html="request.description" class="whitespace-pre-wrap"/>
                                    </template>
                                </position-operations-content>

                                <wrapper grow>
                                    <form-btn @success="handleWithdrawnRequest(request)"
                                              :action="$api('user_jobs.destroy', request.id)" method="PUT"
                                              color="warning"
											  :data="{token: props.token}"
                                              outlined
                                              :confirm="{title: $t('confirm.job_application.destroy.title'), message: $t('confirm.job_application.destroy.message', {job: request.job.title}), type: 'warning'}">
                                        <icon :icon="icons.pending"/>
                                        <span>{{ $t('action.withdraw') }}</span>
                                    </form-btn>
                                </wrapper>
                            </expansion-panel>
                        </expansion-panels>
                    </template>
                </template>

                <alert v-else-if="props.authRelations.is_member" :color="$config.public.colors.success" no-spacing
					   :content="$t('text.member.existing', {group: group.display_name, job: existingJobTitle})"
                       :button="$t('action.to_member_page')"
                       :to="$web(`${props.group.model_name}s.show.membership`, props.group._key)"
                       :icon="icons.check"/>

                <wrapper v-else-if="props.group.defaultMemberJob" center grow class="mt-16">
                    <btn @click="openApplyModal" :color="props.group.color ?? $config.public.colors.success" large>
                        <span>{{ props.group.model_name === 'event' ? $t('action.become_a_volunteer') : $t('action.become_a_member') }}</span>
                    </btn>
                </wrapper>

                <lazy-missing-info v-else :text="$t('missing.group.join_default_job', {group: props.group.display_name})"/>

            </client-only>
        </template>

        <lazy-job-application-form-modal v-if="showApplyModal && props.group.defaultMemberJob" v-model="showApplyModal" :group="props.group" :job="props.group.defaultMemberJob" :title="props.group.model_name === 'event' ? $t('action.become_a_volunteer') : $t('action.request_membership')" @success="handleSentRequest"/>

        <lazy-job-invitation-accept-form-modal v-if="showAcceptModal" v-model="showAcceptModal" :job="selectedJob"
                                               :group="props.group"
                                               :title="`${$t('confirm.job_invitation.accept.title')}: ${selectedJob.club ? `${selectedJob.title} (${selectedJob.club.display_name})` : selectedJob.title}`"
                                               :description="$t('confirm.job_invitation.accept.message', {job: selectedJob.club ? `${selectedJob.title} (${selectedJob.club.display_name})` : selectedJob.title})"
                                               :invitation="selectedInvitation"
                                               :invitation-token="props.token"
                                               :invitation-code="props.code"
                                               @success="handleAcceptedMembership"/>
    </div>
</template>

<script setup lang="ts">
import {join} from "@spoferan/spoferan-ts-core";
import type {ClubPreview, EventPreview} from "@spoferan/spoferan-ts-core";
import {mdiAccountClock, mdiCheck, mdiClose} from "@mdi/js";
import {checkIcon} from "@spoferan/nuxt-spoferan/icons";

const {$auth, $apiFetch, $api} = useNuxtApp();
const {t} = useI18n();

const props = defineProps<{
	group: ClubPreview |EventPreview,

	// The auth relations on the group
	authRelations: {},

	// The group member token to fetch the position invitations even when not authenticated
	token?: string,

	// The invitation code for the positions in which to join the group
	code?: string
}>();

const emit = defineEmits(['accepted', 'declined', 'requested']);

const {reactiveData: pendingUserJobs, pending, error, refresh} = props.token || $auth.loggedIn ? (await useApiFetch($api(`${props.group.model_name}s.members.auth_requests`, props.group.id), {
	auth: true,
	params: {
		token: props.token
	}
})) : {reactiveData: {requests: ref([]), invitations: ref([])}, pending: false, error: null, refresh: null};

const pendingInvitations = computed(() => {
	return pendingUserJobs.value?.invitations ?? [];
});

const pendingRequests = computed(() => {
	return pendingUserJobs.value?.requests ?? [];
});

const {reactiveData: joinCodeJobs} = props.code ? (await useApiFetch($api(`${props.group.model_name}s.members.join.resolve`, [props.group.id, props.code]))) : {reactiveData: ref([]), pending: false, error: null, refresh: null}

const invitedCodeJobs = computed(() => {
	return joinCodeJobs.value ? joinCodeJobs.value.filter(job => {
		// We prefer to display the specific user invitations
		const specificInvitedJobIds = pendingInvitations.value.map(invitation => invitation.job.id);
		if (specificInvitedJobIds.includes(job.id)) {
			return false;
		}

		// We do not display invited code jobs, if already assigned
		const authUserJobIds = (props.authRelations?.jobs ?? []).map(job => job.id);
		if (authUserJobIds.includes(job.id)) {
			return false;
		}

		return true;
	}) : []
});

const existingJobTitle = computed(() => {
	if (props.authRelations.is_member && props.authRelations.jobs?.length) {
		return join(props.authRelations.jobs.map(job => job.title), ', ', ` ${t('conjunction.and')} `);
	}

	return t('label.member');
});

const selectedJob = ref(null);
const selectedInvitation = ref(null);
const showApplyModal = ref(false);
const showAcceptModal = ref(false);

const icons = {
	accept: mdiCheck,
	decline: mdiClose,
	pending: mdiAccountClock,
	check: checkIcon,
};

function handleAcceptedMembership(membership) {
	if (selectedJob.value) {
		removeCodeJob(selectedJob.value);
	}

	if (selectedInvitation.value) {
		removePendingInvitation(selectedInvitation.value);
	}

	emit('accepted', membership);
}

function removePendingInvitation(invitation) {
	const invitationIndex = pendingInvitations.value.findIndex(itInvitation => itInvitation.id === invitation.id);
	if (invitationIndex > -1) {
		pendingInvitations.value.splice(invitationIndex, 1);
	}
}

function handleDeclinedInvitation(invitation) {
	removePendingInvitation(invitation);
	emit('declined', invitation);
}

function removeCodeJob(job) {
	const codeJobIndex = invitedCodeJobs.value.findIndex(codeJob => codeJob.id === job.id);
	if (codeJobIndex > -1) {
		invitedCodeJobs.value.splice(codeJobIndex, 1);
	}
}

function handleDeclinedJob(job) {
	removeCodeJob(job);
	emit('declined', job);
}

function handleWithdrawnRequest(request) {
	const requestIndex = pendingRequests.value.findIndex(itRequest => itRequest.id === request.id);
	if (requestIndex > -1) {
		pendingRequests.value.splice(requestIndex, 1);
	}
	emit('declined', request);
}

function handleSentRequest(request) {
	emit('requested', request);
	pendingRequests.value.push(request);
}

function openApplyModal() {
	showApplyModal.value = true;
}

function openAcceptModal(job, invitation = null) {
	selectedJob.value = job;
	selectedInvitation.value = invitation;
	nextTick(function () {
		showAcceptModal.value = true;
	});
}
</script>